import React from 'react';
import { useSelector } from 'react-redux';
import { Field, reduxForm, getFormValues } from 'redux-form';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import FieldInput from '../../form/FieldInput';
import * as validators from '../../form/validators';
import Button from '../../../common/buttons/onboarding/OnboardingButton';
import Heading from '../../../common/heading/Heading';
import AccountNavLink from '../../../common/links/AccountNavLink';
import {
	LOGIN,
	ACCOUNT_FORGOT_PASSWORD,
	ACCOUNT_FORGOT_EMAIL,
	ONBOARDING_LOGIN_EMAIL_FIELD,
	ONBOARDING_PASSWORD_FIELD,
	ONBOARDING_LOGIN_EMAIL_TITLE,
	ONBOARDING_LOGIN_NO_ACCOUNT,
	ONBOARDING_LOGIN_REGISTER,
	ONBOARDING_LOGIN_EMAIL_TITLE_RTLXL,
	ONBOARDING_LOGIN_EMAIL_TITLE_WITH_BUNDLE,
} from '../../../../constants/Copy';
import { ROUTE_FORGOT_PASSWORD, ROUTE_REGISTER } from '../../../../constants/Routes';
import Message from '../../../common/message/Message';
import { getConfig } from '../../../../apiConfig';
import { initiateTrackStructEvent } from '../../../analytics/Snowplow';
import {
	EVT_LOGIN,
	EVT_RECOVER_PASSWORD_START,
	EVT_SUBSCRIPTION_START,
	EVT_ELEMENT_INTERACTION,
	EVT_ELEMENT_TYPE_HYPERLINK,
	EVT_ELEMENT_NAME_PASSWORD_RECOVERY,
	EVT_ELEMENT_STYLE_TERNARY,
	EVT_REGISTER,
} from '../../../../constants/SnowplowEvents';

import './LoginEmailForm.scss';

const EmailLoginForm = ({
	submitting,
	invalid,
	clearSubmitErrors,
	showMigratedTitleAndHideRegisterLink = true,
	handleSubmit,
	hasPreselectedBundle,
}) => {
	const { identity_host } = getConfig();

	const values = useSelector((state) => getFormValues('emailLogin')(state));

	const doSubmit = (event) => {
		handleSubmit(event);
	};

	return (
		<form
			id="loginForm"
			name="loginForm"
			method="post"
			onSubmit={doSubmit}
			className="onboarding__form onboarding-login-email"
		>
			<Heading className="onboarding-login-email__title">
				{!showMigratedTitleAndHideRegisterLink ? (
					<Message
						message={
							hasPreselectedBundle
								? ONBOARDING_LOGIN_EMAIL_TITLE_WITH_BUNDLE
								: ONBOARDING_LOGIN_EMAIL_TITLE
						}
					/>
				) : (
					<>
						<span>&#128075; </span>
						<Message message={ONBOARDING_LOGIN_EMAIL_TITLE_RTLXL} />
					</>
				)}
			</Heading>

			<Field
				name="email"
				type="email"
				testId="email-input"
				component={FieldInput}
				label={ONBOARDING_LOGIN_EMAIL_FIELD}
				onChange={clearSubmitErrors}
			/>

			<Field
				name="password"
				type="password"
				testId="password-input"
				component={FieldInput}
				label={ONBOARDING_PASSWORD_FIELD}
				onChange={clearSubmitErrors}
			/>

			<div className="onboarding__options--forgot-password-email-wrapper">
				<div>
					<Link
						data-test="password-forgotten-link"
						to={ROUTE_FORGOT_PASSWORD}
						className="onboarding__link"
						onClick={async () => {
							await initiateTrackStructEvent({
								category: EVT_LOGIN,
								action: EVT_RECOVER_PASSWORD_START,
								label: ACCOUNT_FORGOT_PASSWORD,

								rtlElementContext: {
									elementInteraction: EVT_ELEMENT_INTERACTION,
									elementGroup: EVT_LOGIN,
									elementType: EVT_ELEMENT_TYPE_HYPERLINK,
									elementName: EVT_ELEMENT_NAME_PASSWORD_RECOVERY,
									elementLabel: ACCOUNT_FORGOT_PASSWORD,
									elementStyle: EVT_ELEMENT_STYLE_TERNARY,
								},
							});
						}}
					>
						{ACCOUNT_FORGOT_PASSWORD}
					</Link>
					<span className="text--white"> of</span>
				</div>
				<a
					target="_blank"
					rel="noopener noreferrer nofollow"
					data-test="email-forgotten-link"
					href={`${identity_host}/oidc/forgot-email?client_id=videoland`}
					className="onboarding__link"
				>
					{ACCOUNT_FORGOT_EMAIL}
				</a>
			</div>

			<Button
				type="submit"
				loading={submitting}
				disabled={invalid || !validators.hasFormValues(values, 'email', 'password')}
				className="onboarding-login-email__button"
				testId="login-button"
				name="login"
			>
				{LOGIN}
			</Button>
			{!showMigratedTitleAndHideRegisterLink && (
				<AccountNavLink>
					{ONBOARDING_LOGIN_NO_ACCOUNT}{' '}
					<Link
						className="onboarding__link accent"
						tabIndex="0"
						to={ROUTE_REGISTER}
						onClick={() => {
							initiateTrackStructEvent({
								category: EVT_LOGIN,
								action: EVT_SUBSCRIPTION_START,
								label: ONBOARDING_LOGIN_REGISTER,

								rtlElementContext: {
									elementInteraction: EVT_ELEMENT_INTERACTION,
									elementGroup: EVT_REGISTER,
									elementType: EVT_ELEMENT_TYPE_HYPERLINK,
									elementName: EVT_REGISTER,
									elementLabel: ONBOARDING_LOGIN_REGISTER,
									elementStyle: EVT_ELEMENT_STYLE_TERNARY,
								},
							});
						}}
					>
						{ONBOARDING_LOGIN_REGISTER}
					</Link>
				</AccountNavLink>
			)}
		</form>
	);
};

EmailLoginForm.propTypes = {
	handleSubmit: PropTypes.func.isRequired,
	values: PropTypes.object,
	onSocialLogin: PropTypes.func.isRequired,
	showMigratedTitleAndHideRegisterLink: PropTypes.bool,
};

export default reduxForm({
	form: 'emailLogin',
	validate: validators.validateEmail,
	destroyOnUnmount: true, // keep this email field insync with email field in other forms
})(EmailLoginForm);
